<template>
    <div v-if="companyId && enabledDashboard" :class="['mobex-chatbot-root', positionClass]">
        <button
            class="mobex-chat-btn"
            :style="btnStyle"
            :title="theme.chatName"
            @click="toggleWindow"
        >
            <img v-if="theme.avatarUrl" :src="theme.avatarUrl" alt="Bot" @error="onAvatarError"/>
            <span v-else v-html="icons[theme.buttonIcon] || icons.chat"/>
        </button>

        <div v-show="windowOpen" class="mobex-chat-window" :style="windowStyle">
            <div
                class="mobex-chat-resize-handle"
                :title="'Drag to resize'"
                @mousedown="startResize"
                @touchstart="startResize"
            />
            <div class="mobex-chat-header" :style="headerStyle">
                <div class="mobex-chat-header-left">
                    <div class="mobex-chat-header-avatar">
                        <img v-if="theme.avatarUrl" :src="theme.avatarUrl" alt="Bot" @error="onAvatarError"/>
                        <span v-else v-html="icons.bot"/>
                    </div>
                    <div>
                        <div class="mobex-chat-title">{{ theme.chatName }}</div>
                        <div class="mobex-chat-subtitle">{{ theme.welcomeTitle }}</div>
                    </div>
                </div>
                <div class="mobex-chat-header-actions">
                    <button class="mobex-chat-action-btn mobex-chat-reset-btn" title="Clear conversation" @click="resetConversation">
                        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path fill="currentColor" d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.74 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
                        <span>Reset</span>
                    </button>
                    <button class="mobex-chat-action-btn mobex-chat-close-btn" title="Close" @click="windowOpen = false">
                        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </button>
                </div>
            </div>

            <div ref="messagesEl" class="mobex-chat-messages" :style="messagesStyle">
                <div
                    v-for="(m, i) in messages"
                    :key="i"
                    :class="['mobex-chat-msg', m.role]"
                    :style="msgStyle(m.role)"
                >
                    <span v-if="m.role === 'bot'" v-html="renderMarkdown(m.text)"/>
                    <span v-else>{{ m.text }}</span>
                </div>
                <div v-if="isTyping" class="mobex-chat-typing-wrap">
                    <div class="mobex-chat-typing">
                        <span/><span/><span/>
                    </div>
                </div>
            </div>

            <div v-if="quickReplies.length" class="mobex-chat-qr-row" :style="qrRowStyle">
                <button
                    v-for="qr in quickReplies"
                    :key="qr.id || qr.label"
                    class="mobex-chat-qr-btn"
                    :style="qrBtnStyle"
                    @click="sendQuickReply(qr)"
                >{{ qr.label }}</button>
            </div>

            <div v-if="showAuth" class="mobex-chat-auth">
                <div class="mobex-chat-auth-msg">We hit a small snag connecting to the assistant.</div>
                <div class="mobex-chat-auth-hint">This can happen after a period of inactivity. Tap the button below to get back to the conversation.</div>
                <button class="mobex-chat-retry" :style="retryBtnStyle" @click="retryConnect">&#8635; Reconnect</button>
            </div>

            <div class="mobex-chat-input-area">
                <textarea
                    ref="inputEl"
                    v-model="inputText"
                    rows="1"
                    placeholder="Ask a health question…"
                    class="mobex-chat-input"
                    :style="inputStyle"
                    @keydown="handleKeydown"
                    @input="autosize"
                />
                <button
                    class="mobex-chat-send"
                    :style="sendBtnStyle"
                    :disabled="!canSend"
                    @click="sendTyped"
                >
                    <span class="mobex-chat-send-arrow" aria-hidden="true">&#10148;</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { io } from 'socket.io-client';

const DEFAULT_THEME = {
    primaryColor: '#1a73e8',
    secondaryColor: '#f8f9fa',
    textColor: '#202124',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    fontSize: '14px',
    borderRadius: '16px',
    position: 'bottom-right',
    buttonIcon: 'chat',
    chatName: 'MobexHealth Assistant',
    welcomeTitle: 'Ask me anything about health & Mobex',
    avatarUrl: '',
};

const SEND_ICON_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>';

// xmlns is REQUIRED — without it, Safari (and sometimes Firefox) parse v-html
// SVG as unknown HTML elements with zero size, so the icon never renders.
const ICONS = {
    chat: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>',
    message: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z"/></svg>',
    help: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>',
    support: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M12 1C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1zm-1 16H9V9h2v8zm4 0h-2V9h2v8zM12 7C10.9 7 10 6.1 10 5s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>',
    // Friendly robot for the chat header profile spot when no custom avatar is set.
    // Material "smart_toy" icon path; antenna + rounded head + two eyes + mouth.
    bot: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="#fff"><path d="M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3v4c0 1.66 1.34 3 3 3v3c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3c1.66 0 3-1.34 3-3v-4c0-1.66-1.34-3-3-3zM7.5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S9.83 13 9 13s-1.5-.67-1.5-1.5zM16 17H8v-2h8v2zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z"/></svg>',
};

const MAX_CACHED = 100;

// Role-neutral fallback shown only when the server sends no quick replies
// (e.g. OpenSearch unseeded). The server's quick_replies emit is the source
// of truth — it role-filters onboarding Q&As + company FAQs in
// chatbot.socket.js (buildQuickRepliesForSocket). Admin-leaning chips like
// "How many users do we have?" must not live in this static list because
// it has no role context at render time.
const VUE_INITIAL_SUGGESTIONS = [
    { id: 'wic',     label: '🥗 WIC Benefits' },
    { id: 'teladoc', label: '📱 Teladoc' },
];

function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

// Markdown links [text](url) and bare URLs are extracted to placeholders
// BEFORE the rest of the pipeline so emphasis/list/<br> rules can't smash
// the <a> attributes. All anchors get target="_blank" rel="noopener noreferrer".
function renderMarkdown(text) {
    let escaped = String(text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const anchors = [];
    const stash = (html) => { anchors.push(html); return ' A' + (anchors.length - 1) + ' '; };
    const anchor = (href, label) =>
        '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';

    escaped = escaped.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)"'<>]+|mailto:[^\s)"'<>]+)\)/g,
        (_, label, url) => stash(anchor(url, label))
    );
    escaped = escaped.replace(
        /\b(https?:\/\/[^\s"'<> ]+|mailto:[^\s"'<> ]+)/g,
        (m) => {
            let trail = '';
            let url = m;
            const trailMatch = /[.,!?;:)\]}>]+$/.exec(url);
            if (trailMatch) { trail = trailMatch[0]; url = url.slice(0, -trail.length); }
            return stash(anchor(url, url)) + trail;
        }
    );

    const html = escaped
        .replace(/```[\s\S]*?```/g, (m) => '<pre><code>' + m.slice(3, -3) + '</code></pre>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^---$/gm, '<hr>')
        .replace(/^\|[-: |]+\|\s*\n?/gm, '')
        .replace(/^\|(.+)\|$/gm, (row) => {
            const cells = row.slice(1, -1).split('|').map((c) => c.trim());
            return '<tr>' + cells.map((c) => '<td>' + c + '</td>').join('') + '</tr>';
        })
        .replace(/(<tr>[\s\S]*?<\/tr>\s*)+/g, (t) => '<table>' + t.replace(/<\/tr>\s+<tr>/g, '</tr><tr>').replace(/\s+$/, '') + '</table>')
        .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>[\s\S]*?<\/li>\s*)+/g, (l) => '<ul>' + l.replace(/<\/li>\s+<li>/g, '</li><li>').replace(/\s+$/, '') + '</ul>')
        .replace(/\n\n+/g, '</p><p>')
        .replace(/\n/g, '<br>');

    return html.replace(/ A(\d+) /g, (_, i) => anchors[+i]);
}

export default {
    name: 'ChatbotWidget',
    props: {
        companyId: { type: [String, Number], required: true },
        firstName: { type: String, default: '' },
    },
    data() {
        return {
            theme: { ...DEFAULT_THEME },
            icons: ICONS,
            sendIconSvg: SEND_ICON_SVG,
            windowOpen: false,
            messages: [],
            quickReplies: [],
            inputText: '',
            isTyping: false,
            connected: false,
            waitingResponse: false,
            showAuth: false,
            socket: null,
            enabledDashboard: false,
            userHasInteracted: false,
            heartbeatTimer: null,
            onlineHandler: null,
            visibilityHandler: null,
            windowWidth: 360,
            windowHeight: 520,
            resizing: false,
            resizeStart: null,
            resizeMoveHandler: null,
            resizeEndHandler: null,
        };
    },
    computed: {
        serverUrl() {
            const apiDomain = process.env.VUE_APP_API_DOMAIN || '';
            if (apiDomain && /^https?:\/\//.test(apiDomain)) return apiDomain.replace(/\/$/, '');
            return window.location.origin;
        },
        socketNs() { return this.serverUrl + '/chatbot'; },
        sessionStorageKey() { return 'mobex_chat_session_' + this.companyId; },
        cacheKey() { return 'mobex_chat_history_' + (this.companyId || 'default'); },
        greetingFlagKey() { return 'mobex_chat_greeting_shown_' + (this.companyId || 'default'); },
        userLocale() {
            const raw = navigator.language || navigator.userLanguage || 'en';
            return raw.split('-')[0].toLowerCase();
        },
        canSend() { return this.connected && !this.waitingResponse && this.inputText.trim().length > 0; },
        positionClass() { return this.theme.position === 'bottom-left' ? 'pos-left' : 'pos-right'; },
        btnStyle() {
            return { background: this.theme.primaryColor };
        },
        windowStyle() {
            return {
                borderRadius: this.theme.borderRadius,
                fontFamily: this.theme.fontFamily,
                fontSize: this.theme.fontSize,
                // expose primary color so v-html links inside message bubbles
                // pick it up via var(--mobex-primary).
                '--mobex-primary': this.theme.primaryColor,
                width: this.windowWidth + 'px',
                height: this.windowHeight + 'px',
            };
        },
        sizeStorageKey() { return 'mobex_chat_size_' + (this.companyId || 'default'); },
        headerStyle() { return { background: this.theme.primaryColor }; },
        messagesStyle() { return { background: this.theme.secondaryColor }; },
        qrRowStyle() { return { background: this.theme.secondaryColor }; },
        qrBtnStyle() {
            return {
                borderColor: this.theme.primaryColor,
                color: this.theme.primaryColor,
                '--qr-primary': this.theme.primaryColor,
            };
        },
        retryBtnStyle() { return { background: this.theme.primaryColor }; },
        sendBtnStyle() {
            return {
                background: this.theme.primaryColor,
                opacity: this.canSend ? 1 : 0.45,
            };
        },
        inputStyle() { return {}; },
    },
    watch: {
        companyId: {
            immediate: false,
            handler(val, oldVal) {
                if (val && val !== oldVal) {
                    this.teardown();
                    this.bootstrap();
                }
            },
        },
    },
    mounted() {
        this.bootstrap();
    },
    beforeDestroy() {
        this.teardown();
    },
    methods: {
        bootstrap() {
            if (!this.companyId) return;
            this.getChatSessionKey();
            this.loadCachedMessages();
            this.loadSavedSize();
            this.fetchConfig();
            this.onlineHandler = () => { if (this.socket && !this.connected) this.socket.connect(); };
            this.visibilityHandler = () => {
                if (document.visibilityState === 'visible' && this.socket && !this.connected) {
                    this.socket.connect();
                }
            };
            window.addEventListener('online', this.onlineHandler);
            document.addEventListener('visibilitychange', this.visibilityHandler);
        },
        teardown() {
            this.stopHeartbeat();
            if (this.onlineHandler) window.removeEventListener('online', this.onlineHandler);
            if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
            if (this.resizeMoveHandler) {
                window.removeEventListener('mousemove', this.resizeMoveHandler);
                window.removeEventListener('touchmove', this.resizeMoveHandler);
            }
            if (this.resizeEndHandler) {
                window.removeEventListener('mouseup', this.resizeEndHandler);
                window.removeEventListener('touchend', this.resizeEndHandler);
            }
            this.resizeMoveHandler = null;
            this.resizeEndHandler = null;
            this.resizing = false;
            this.onlineHandler = null;
            this.visibilityHandler = null;
            if (this.socket) {
                this.socket.removeAllListeners();
                this.socket.disconnect();
                this.socket = null;
            }
            this.connected = false;
        },

        getChatSessionKey() {
            try {
                const existing = localStorage.getItem(this.sessionStorageKey);
                if (existing) return existing;
                const fresh = generateUUID();
                localStorage.setItem(this.sessionStorageKey, fresh);
                return fresh;
            } catch (e) {
                return null;
            }
        },

        cacheMessage(role, text) {
            try {
                const history = JSON.parse(sessionStorage.getItem(this.cacheKey) || '[]');
                history.push({ role, text });
                const trimmed = history.length > MAX_CACHED ? history.slice(-MAX_CACHED) : history;
                sessionStorage.setItem(this.cacheKey, JSON.stringify(trimmed));
            } catch (e) { /* sessionStorage unavailable */ }
        },
        loadCachedMessages() {
            try {
                const history = JSON.parse(sessionStorage.getItem(this.cacheKey) || '[]');
                this.messages = history;
                return history.length > 0;
            } catch (e) { return false; }
        },
        clearMessageCache() {
            try { sessionStorage.removeItem(this.cacheKey); } catch (e) { /* ignore */ }
        },

        addMessage(role, text) {
            this.messages.push({ role, text });
            this.cacheMessage(role, text);
            this.$nextTick(this.scrollBottom);
        },
        scrollBottom() {
            const el = this.$refs.messagesEl;
            if (el) el.scrollTop = el.scrollHeight;
        },
        renderMarkdown,
        msgStyle(role) {
            if (role === 'user') return { background: this.theme.primaryColor, color: '#fff' };
            return { color: this.theme.textColor };
        },

        async fetchConfig() {
            try {
                const url = this.serverUrl + '/chatbot-builder/public-config';
                const res = await axios.get(url, {
                    params: { companyId: this.companyId },
                    withCredentials: true,
                });
                if (res?.data?.data) this.applyTheme(res.data.data);
            } catch (e) {
                // Network/config failure — keep defaults.
            }
        },
        applyTheme(cfg) {
            if (!cfg) return;
            this.enabledDashboard = cfg.enabledDashboard === true;
            if (!this.enabledDashboard) {
                if (this.socket) { this.socket.removeAllListeners(); this.socket.disconnect(); this.socket = null; }
                this.windowOpen = false;
                return;
            }
            const s = cfg.styling || {};
            const w = cfg.welcomeMessage || {};
            const next = { ...this.theme };
            ['primaryColor', 'secondaryColor', 'textColor', 'fontFamily', 'fontSize', 'borderRadius', 'position', 'buttonIcon']
                .forEach((k) => { if (s[k]) next[k] = s[k]; });
            if (cfg.name) next.chatName = cfg.name;
            if (w.title) next.welcomeTitle = w.title;
            if (w.avatarUrl) next.avatarUrl = w.avatarUrl;
            this.theme = next;
            if (w.message && this.messages.length === 0) {
                const welcomeText = w.title ? ('**' + w.title + '**\n\n' + w.message) : w.message;
                this.addMessage('bot', welcomeText);
            }
        },
        onAvatarError(e) { e.target.style.display = 'none'; },

        connect() {
            if (this.socket) return;
            const manualSession =
                window.MOBEX_SESSION_ID ||
                (() => { try { return localStorage.getItem('mobex_session_id'); } catch (e) { return ''; } })() ||
                '';
            const opts = {
                withCredentials: true,
                transports: ['websocket', 'polling'],
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                timeout: 20000,
            };
            const authPayload = { companyId: String(this.companyId), firstName: this.firstName, locale: this.userLocale };
            if (manualSession) authPayload.sessionId = manualSession;
            opts.auth = authPayload;

            const socket = io(this.socketNs, opts);
            this.socket = socket;

            socket.on('connect', () => {
                this.connected = true;
                this.showAuth = false;
                this.startHeartbeat();
            });
            socket.on('greeting', (data) => {
                if (this.hasShownGreeting()) return;
                this.addMessage('bot', data?.content || '');
                this.markGreetingShown();
            });
            socket.on('connect_error', (err) => {
                this.connected = false;
                this.stopHeartbeat();
                this.addMessage('bot', this.friendlyConnectError(err));
                this.showAuth = true;
                socket.disconnect();
                this.socket = null;
            });
            socket.on('typing', (data) => {
                if (data?.status) { this.isTyping = true; this.waitingResponse = true; }
                else { this.isTyping = false; this.waitingResponse = false; }
                this.$nextTick(this.scrollBottom);
            });
            socket.on('response', (data) => this.addMessage('bot', data?.content || ''));
            socket.on('quick_replies', (replies) => {
                // Trust server's role-filtered list when present. Only fall
                // back to the static neutral list when the server has nothing
                // (unseeded OpenSearch, no company FAQs configured).
                const list = (replies || []).slice(0, 3);
                this.quickReplies = list.length ? list : VUE_INITIAL_SUGGESTIONS;
            });
            socket.on('reset_ok', () => {
                this.messages = [];
                this.quickReplies = [];
                this.userHasInteracted = false;
                this.clearMessageCache();
                this.clearGreetingFlag();
            });
            socket.on('error', (data) => {
                this.isTyping = false;
                this.waitingResponse = false;
                this.addMessage('bot', data?.message || 'We\'re experiencing a brief service interruption. If you need immediate assistance, please contact our support team. We apologize for the inconvenience.');
            });
            socket.on('disconnect', () => {
                this.connected = false;
                this.stopHeartbeat();
            });
        },

        friendlyConnectError(err) {
            const raw = (err && err.message) ? String(err.message).trim() : '';
            const lower = raw.toLowerCase();
            // Cryptic Socket.IO transport-level errors — translate to something a user can act on.
            const isTransport = !raw
                || lower === 'websocket error'
                || lower === 'xhr poll error'
                || lower === 'timeout'
                || lower.startsWith('transport ')
                || lower.includes('network error')
                || lower.includes('failed to fetch');
            if (isTransport) {
                return navigator.onLine === false
                    ? 'It looks like you\'re offline. Please check your internet connection and try again. If the issue persists, please contact admin directly.'
                    : 'I\'m having trouble reaching the assistant right now. Please check your connection and tap **Reconnect** below to try again. If the issue persists, please contact admin directly.';
            }
            // Server-sent reasons (auth/session) are already worded for end users — pass through.
            return raw;
        },

        startHeartbeat() {
            this.stopHeartbeat();
            this.heartbeatTimer = setInterval(() => {
                if (this.socket && this.connected) {
                    try { this.socket.emit('ping'); } catch (e) { /* ignore */ }
                }
            }, 25000);
        },
        stopHeartbeat() {
            if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
        },

        loadSavedSize() {
            try {
                const raw = localStorage.getItem(this.sizeStorageKey);
                if (!raw) return;
                const { w, h } = JSON.parse(raw) || {};
                if (typeof w === 'number' && typeof h === 'number') {
                    const { w: cw, h: ch } = this.clampSize(w, h);
                    this.windowWidth = cw;
                    this.windowHeight = ch;
                }
            } catch (e) { /* ignore */ }
        },
        saveSize() {
            try {
                localStorage.setItem(this.sizeStorageKey, JSON.stringify({ w: this.windowWidth, h: this.windowHeight }));
            } catch (e) { /* ignore */ }
        },
        clampSize(w, h) {
            const minW = 320;
            const minH = 400;
            const maxW = Math.max(minW, window.innerWidth - 48);
            const maxH = Math.max(minH, window.innerHeight - 120);
            return {
                w: Math.min(Math.max(w, minW), maxW),
                h: Math.min(Math.max(h, minH), maxH),
            };
        },
        startResize(e) {
            e.preventDefault();
            const point = e.touches ? e.touches[0] : e;
            this.resizing = true;
            this.resizeStart = {
                x: point.clientX,
                y: point.clientY,
                w: this.windowWidth,
                h: this.windowHeight,
                leftPositioned: this.theme.position === 'bottom-left',
            };
            this.resizeMoveHandler = (ev) => this.onResizeMove(ev);
            this.resizeEndHandler = () => this.endResize();
            window.addEventListener('mousemove', this.resizeMoveHandler);
            window.addEventListener('mouseup', this.resizeEndHandler);
            window.addEventListener('touchmove', this.resizeMoveHandler, { passive: false });
            window.addEventListener('touchend', this.resizeEndHandler);
        },
        onResizeMove(e) {
            if (!this.resizing || !this.resizeStart) return;
            if (e.cancelable) e.preventDefault();
            const point = e.touches ? e.touches[0] : e;
            const dx = point.clientX - this.resizeStart.x;
            const dy = point.clientY - this.resizeStart.y;
            // Widget sits at the bottom; dragging the top corner outward (up + away)
            // should GROW the widget. For bottom-right: moving left (negative dx)
            // increases width. For bottom-left: moving right (positive dx) increases width.
            // Height grows when dragging up (negative dy).
            const widthDelta = this.resizeStart.leftPositioned ? dx : -dx;
            const heightDelta = -dy;
            const { w, h } = this.clampSize(
                this.resizeStart.w + widthDelta,
                this.resizeStart.h + heightDelta,
            );
            this.windowWidth = w;
            this.windowHeight = h;
        },
        endResize() {
            this.resizing = false;
            this.resizeStart = null;
            window.removeEventListener('mousemove', this.resizeMoveHandler);
            window.removeEventListener('mouseup', this.resizeEndHandler);
            window.removeEventListener('touchmove', this.resizeMoveHandler);
            window.removeEventListener('touchend', this.resizeEndHandler);
            this.resizeMoveHandler = null;
            this.resizeEndHandler = null;
            this.saveSize();
            this.$nextTick(this.scrollBottom);
        },

        toggleWindow() {
            this.windowOpen = !this.windowOpen;
            if (this.windowOpen) {
                this.fetchConfig();
                if (!this.socket) this.connect();
                this.$nextTick(this.scrollBottom);
            }
        },
        resetConversation() {
            if (this.socket && this.connected) this.socket.emit('reset');
        },
        retryConnect() {
            if (this.socket) { this.socket.removeAllListeners(); this.socket.disconnect(); this.socket = null; }
            this.showAuth = false;
            this.messages = [];
            this.clearMessageCache();
            this.clearGreetingFlag();
            this.connect();
        },

        hasShownGreeting() {
            try { return sessionStorage.getItem(this.greetingFlagKey) === '1'; } catch (e) { return false; }
        },
        markGreetingShown() {
            try { sessionStorage.setItem(this.greetingFlagKey, '1'); } catch (e) { /* ignore */ }
        },
        clearGreetingFlag() {
            try { sessionStorage.removeItem(this.greetingFlagKey); } catch (e) { /* ignore */ }
        },

        sendTyped() {
            const text = this.inputText.trim();
            if (!text) return;
            this.sendMessage(text);
            this.inputText = '';
            this.autosize();
        },
        sendQuickReply(qr) {
            this.quickReplies = [];
            this.sendMessage(qr.label);
        },
        sendMessage(text) {
            if (!this.connected || this.waitingResponse || !text) return;
            this.userHasInteracted = true;
            this.quickReplies = [];
            this.addMessage('user', text);
            this.socket.emit('message', { content: text });
            this.waitingResponse = true;
        },
        handleKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.sendTyped(); }
        },
        autosize() {
            const el = this.$refs.inputEl;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 100) + 'px';
        },
    },
};
</script>

<style scoped>
.mobex-chatbot-root { position: fixed; bottom: 0; z-index: 9999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.mobex-chatbot-root.pos-right { right: 24px; }
.mobex-chatbot-root.pos-left  { left: 24px; }

/* ── Floating launcher button ─────────────────────────────────────────── */
.mobex-chat-btn {
    position: fixed; bottom: 24px; width: 60px; height: 60px; border-radius: 50%;
    border: none; cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,.15), 0 12px 36px rgba(0,0,0,.18);
    display: flex; align-items: center; justify-content: center;
    transition: transform .18s ease, box-shadow .18s ease; overflow: hidden; z-index: 9999;
}
.mobex-chatbot-root.pos-right .mobex-chat-btn { right: 24px; }
.mobex-chatbot-root.pos-left  .mobex-chat-btn { left: 24px; }
.mobex-chat-btn:hover  { transform: translateY(-2px) scale(1.04); box-shadow: 0 6px 16px rgba(0,0,0,.18), 0 16px 44px rgba(0,0,0,.22); }
.mobex-chat-btn:active { transform: scale(.97); }
.mobex-chat-btn img    { width: 100%; height: 100%; object-fit: cover; }

/* ── Chat window ──────────────────────────────────────────────────────── */
.mobex-chat-window {
    position: fixed; bottom: 96px; max-width: calc(100vw - 48px);
    max-height: calc(100vh - 120px); background: #fff;
    border: 1px solid rgba(0,0,0,.05);
    box-shadow:
        0 1px 2px rgba(0,0,0,.04),
        0 8px 24px rgba(0,0,0,.08),
        0 24px 64px rgba(0,0,0,.14);
    display: flex; flex-direction: column; overflow: hidden; z-index: 9999;
    animation: mobex-pop-in .22s cubic-bezier(.2,.9,.3,1.1) both;
}
@keyframes mobex-pop-in {
    from { opacity: 0; transform: translateY(8px) scale(.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}
.mobex-chatbot-root.pos-right .mobex-chat-window { right: 24px; }
.mobex-chatbot-root.pos-left  .mobex-chat-window { left: 24px; }

/* ── Resize handle ────────────────────────────────────────────────────── */
.mobex-chat-resize-handle {
    position: absolute; top: 0; width: 20px; height: 20px; z-index: 10001;
    background: transparent;
}
.mobex-chatbot-root.pos-right .mobex-chat-resize-handle { left: 0;  cursor: nwse-resize; }
.mobex-chatbot-root.pos-left  .mobex-chat-resize-handle { right: 0; cursor: nesw-resize; }
.mobex-chat-resize-handle::before {
    content: ''; position: absolute; width: 8px; height: 8px;
    border-top: 2px solid rgba(255,255,255,.75); top: 6px; border-radius: 1px;
}
.mobex-chatbot-root.pos-right .mobex-chat-resize-handle::before { left: 6px;  border-left: 2px solid rgba(255,255,255,.75); }
.mobex-chatbot-root.pos-left  .mobex-chat-resize-handle::before { right: 6px; border-right: 2px solid rgba(255,255,255,.75); }

/* ── Header ───────────────────────────────────────────────────────────── */
.mobex-chat-header {
    color: #fff; padding: 14px 16px; display: flex; align-items: center;
    gap: 10px; justify-content: space-between; flex-shrink: 0;
}
.mobex-chat-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.mobex-chat-header-avatar {
    width: 38px; height: 38px; border-radius: 50%; overflow: hidden;
    background: rgba(255,255,255,.22); display: flex; align-items: center;
    justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(255,255,255,.18);
}
.mobex-chat-header-avatar img { width: 100%; height: 100%; object-fit: cover; }
.mobex-chat-title    { font-weight: 600; font-size: 15px; line-height: 1.2; }
.mobex-chat-subtitle { font-size: 11px; opacity: .85; margin-top: 2px; line-height: 1.2; }

.mobex-chat-header-actions { display: flex; gap: 6px; align-items: center; }
.mobex-chat-action-btn {
    background: rgba(255,255,255,.14);
    border: 1px solid rgba(255,255,255,.22);
    border-radius: 999px;
    color: #fff; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    padding: 5px 11px; font-size: 11.5px; font-weight: 600; letter-spacing: .15px;
    font-family: inherit; line-height: 1;
    transition: background .15s ease, transform .12s ease, border-color .15s ease;
    -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
}
.mobex-chat-action-btn:hover  { background: rgba(255,255,255,.26); border-color: rgba(255,255,255,.36); }
.mobex-chat-action-btn:active { transform: scale(.96); }
.mobex-chat-action-btn svg    { display: block; }

.mobex-chat-close-btn { width: 28px; height: 28px; padding: 0; border-radius: 50%; }

/* ── Messages list ────────────────────────────────────────────────────── */
.mobex-chat-messages {
    flex: 1; overflow-y: auto; padding: 14px 12px 8px;
    display: flex; flex-direction: column; gap: 8px;
    scroll-behavior: smooth;
}
.mobex-chat-messages::-webkit-scrollbar       { width: 6px; }
.mobex-chat-messages::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 3px; }
.mobex-chat-messages::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,.25); }

/* ── Message bubble ───────────────────────────────────────────────────── */
.mobex-chat-msg {
    max-width: 82%; padding: 10px 14px; border-radius: 16px; line-height: 1.45;
    font-size: 13.5px;
    word-wrap: break-word;
    animation: mobex-msg-in .2s ease both;
}
@keyframes mobex-msg-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
}
.mobex-chat-msg.user { align-self: flex-end; border-bottom-right-radius: 5px; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.mobex-chat-msg.bot  {
    align-self: flex-start; background: #fff;
    border-bottom-left-radius: 5px;
    box-shadow: 0 1px 2px rgba(0,0,0,.06), 0 1px 6px rgba(0,0,0,.05);
}
.mobex-chat-msg.bot >>> h1,
.mobex-chat-msg.bot >>> h2,
.mobex-chat-msg.bot >>> h3 { font-size: 1em; font-weight: 600; margin: 6px 0 2px; }
.mobex-chat-msg.bot >>> ul,
.mobex-chat-msg.bot >>> ol { padding-left: 18px; margin: 4px 0; }
.mobex-chat-msg.bot >>> p  { margin: 4px 0; }
.mobex-chat-msg.bot >>> table { border-collapse: collapse; width: 100%; font-size: 12px; }
.mobex-chat-msg.bot >>> td,
.mobex-chat-msg.bot >>> th { border: 1px solid #e0e0e0; padding: 4px 6px; }
.mobex-chat-msg.bot >>> th { background: #f1f3f4; }
.mobex-chat-msg.bot >>> code { background: #f1f3f4; padding: 1px 5px; border-radius: 4px; font-size: 12px; }
.mobex-chat-msg.bot >>> pre  { background: #f1f3f4; padding: 8px; border-radius: 8px; overflow-x: auto; font-size: 12px; }
.mobex-chat-msg.bot >>> hr   { border: none; border-top: 1px solid #e0e0e0; margin: 6px 0; }
.mobex-chat-msg >>> a {
    color: var(--mobex-primary, #1773B1);
    text-decoration: underline;
    word-break: break-word;
}
.mobex-chat-msg >>> a:hover  { text-decoration: none; opacity: .85; }
.mobex-chat-msg.user >>> a   { color: #fff; }

/* ── Typing / loading bubble ──────────────────────────────────────────── */
.mobex-chat-typing-wrap {
    align-self: flex-start;
    background: #fff;
    border-radius: 16px;
    border-bottom-left-radius: 5px;
    padding: 12px 16px;
    box-shadow: 0 1px 2px rgba(0,0,0,.06), 0 1px 6px rgba(0,0,0,.05);
    animation: mobex-msg-in .2s ease both;
}
.mobex-chat-typing { display: flex; gap: 5px; align-items: center; }
.mobex-chat-typing span {
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--mobex-primary, #1773B1);
    animation: mobex-bounce 1.1s ease-in-out infinite;
}
.mobex-chat-typing span:nth-child(2) { animation-delay: .15s; }
.mobex-chat-typing span:nth-child(3) { animation-delay: .3s; }
@keyframes mobex-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: .35; }
    30%           { transform: translateY(-5px); opacity: 1; }
}

/* ── Auth retry panel ─────────────────────────────────────────────────── */
.mobex-chat-auth {
    padding: 16px; background: #fff8e1; border-top: 1px solid #ffe082;
    display: flex; flex-direction: column; gap: 8px; text-align: center;
}
.mobex-chat-auth-msg  { font-size: 12px; color: #b45309; font-weight: 600; }
.mobex-chat-auth-hint { font-size: 11px; color: #5f6368; margin-top: 2px; }
.mobex-chat-retry {
    margin: 8px auto 0; color: #fff; border: none; border-radius: 999px;
    padding: 9px 18px; cursor: pointer; font-size: 12px; font-weight: 600;
    box-shadow: 0 2px 6px rgba(0,0,0,.12);
    transition: transform .12s ease, box-shadow .15s ease;
}
.mobex-chat-retry:hover  { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,0,0,.18); }
.mobex-chat-retry:active { transform: scale(.97); }

/* ── Quick-reply chips ────────────────────────────────────────────────── */
.mobex-chat-qr-row {
    display: flex; flex-wrap: wrap; gap: 6px;
    padding: 8px 12px; border-top: 1px solid rgba(0,0,0,.06);
}
.mobex-chat-qr-btn {
    flex-shrink: 0; background: rgba(255,255,255,.7); border: 1px solid;
    border-radius: 999px; padding: 5px 12px; font-size: 11.5px; font-weight: 500;
    cursor: pointer; white-space: nowrap;
    transition: background .15s ease, color .15s ease, transform .12s ease;
    font-family: inherit; line-height: 1.4;
}
.mobex-chat-qr-btn:hover  { background: var(--qr-primary); color: #fff !important; transform: translateY(-1px); }
.mobex-chat-qr-btn:active { transform: scale(.97); }

/* ── Input area ───────────────────────────────────────────────────────── */
.mobex-chat-input-area {
    display: flex; padding: 10px 12px 12px; gap: 8px;
    border-top: 1px solid rgba(0,0,0,.06); background: #fff; flex-shrink: 0;
    align-items: flex-end;
}
.mobex-chat-input {
    flex: 1; border: 1px solid #e3e6ea; border-radius: 22px;
    padding: 10px 14px; font-size: 13.5px; outline: none; resize: none;
    font-family: inherit; max-height: 100px; overflow-y: auto;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.mobex-chat-input:focus {
    border-color: var(--mobex-primary, #1773B1);
    box-shadow: 0 0 0 3px rgba(23,115,177,.12);
}
.mobex-chat-send {
    border: none; border-radius: 50%; width: 40px; height: 40px;
    /* fallback so the button has a visible background even if the theme
       primary color hasn't loaded yet or the admin saved an empty value.
       The inline :style binding still overrides when primaryColor is set. */
    background: var(--mobex-primary, #1773B1);
    cursor: pointer; flex-shrink: 0; display: flex; align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,.12);
    transition: background .2s, transform .12s ease, box-shadow .15s ease;
}
.mobex-chat-send:hover:not(:disabled)  { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,0,0,.18); }
.mobex-chat-send:active:not(:disabled) { transform: scale(.95); }
.mobex-chat-send:disabled              { cursor: not-allowed; box-shadow: none; }
.mobex-chat-send-arrow {
    color: #fff !important;
    font-size: 20px; line-height: 1;
    display: inline-block; transform: translateX(1px);
    font-weight: 700; pointer-events: none;
}
</style>
